import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Users, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { getOfOtCompare, setActiveSource, AdminOfOtCompareRow } from '../api/activeSource';
import PageHeader from '../components/shared/PageHeader';
import SourceToggle from '../components/SourceToggle';
import SubTabs from '../components/shared/SubTabs';
import { SkelRow, SkelKpi } from '../components/shared/Skeleton';
import { ErrorBanner } from '../components/shared/ErrorBanner';
import { EmptyState } from '../components/shared/EmptyState';

const CPC_TABS = [
  { label: 'Overview', to: '/cpc/overview' },
  { label: 'By page',  to: '/cpc', end: true },
  { label: 'OF vs OT',  to: '/cpc/of-ot' }
];

const fmtCpc = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n) ? `$${n.toFixed(4)}` : '—';

type SortField = 'username' | 'delta' | 'ofCpc' | 'otCpc';
type SortDir = 'asc' | 'desc';

function delta(row: AdminOfOtCompareRow): number | null {
  if (row.ofCpc == null || row.otCpc == null) return null;
  return row.ofCpc - row.otCpc;
}

/** Which source currently wins the listing for this row (null = no override → OF). */
function activeOf(row: AdminOfOtCompareRow): 'OF' | 'OT' {
  return row.activeSource ?? 'OF';
}

/** Which source has the higher bid — null on a tie or missing CPC. */
function higherBidder(row: AdminOfOtCompareRow): 'OF' | 'OT' | null {
  const d = delta(row);
  if (d == null || d === 0) return null;
  return d > 0 ? 'OF' : 'OT';
}

const CpcOfOtComparePage: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('delta');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [pendingUsername, setPendingUsername] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['of-ot-compare'],
    queryFn: getOfOtCompare,
  });

  const stats = useMemo(() => {
    const list = data ?? [];
    const ofAhead = list.filter(r => higherBidder(r) === 'OF').length;
    const otAhead = list.filter(r => higherBidder(r) === 'OT').length;
    const suboptimal = list.filter(r => {
      const winner = higherBidder(r);
      return winner != null && winner !== activeOf(r);
    }).length;
    return { total: list.length, ofAhead, otAhead, suboptimal };
  }, [data]);

  function toggleSort(f: SortField) {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('desc'); }
  }
  const sortIndicator = (f: SortField) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const rows = useMemo(() => {
    let filtered = data ?? [];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter(r => r.username.toLowerCase().includes(q));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      switch (sortField) {
        case 'username': return a.username.localeCompare(b.username) * dir;
        case 'ofCpc': return ((a.ofCpc ?? -1) - (b.ofCpc ?? -1)) * dir;
        case 'otCpc': return ((a.otCpc ?? -1) - (b.otCpc ?? -1)) * dir;
        case 'delta':
        default: {
          const da = delta(a), db = delta(b);
          if (da == null && db == null) return 0;
          if (da == null) return 1;
          if (db == null) return -1;
          return (da - db) * dir;
        }
      }
    });
    return sorted;
  }, [data, search, sortField, sortDir]);

  const handleSetActive = async (username: string, source: 'OF' | 'OT') => {
    setPendingUsername(username);
    try {
      await setActiveSource(username, source);
      queryClient.setQueryData<AdminOfOtCompareRow[]>(['of-ot-compare'], old =>
        old?.map(r => r.username === username ? { ...r, activeSource: source } : r)
      );
    } finally {
      setPendingUsername(null);
    }
  };

  return (
    <>
      <PageHeader
        title="CPC"
        subtitle={<>usernames with a live OF <span className="hf-mono">and</span> a live OT order — pick which wins the open-geo listing</>}
      />

      <div className="hf-content">
        <SubTabs items={CPC_TABS} />

        {error ? (
          <ErrorBanner
            message="Failed to load OF vs OT comparison."
            onRetry={() => refetch()}
          />
        ) : (
          <>
            {isLoading ? (
              <div className="hf-kpi-strip">
                <SkelKpi /><SkelKpi /><SkelKpi /><SkelKpi />
              </div>
            ) : (
              <div className="hf-kpi-strip">
                <div className="hf-kpi">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span className="hf-kpi-label">Dual-source usernames</span>
                    <Users size={13} style={{ color: 'var(--fg-3)' }} />
                  </div>
                  <span className="hf-kpi-value">{stats.total}</span>
                </div>
                <div className="hf-kpi">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span className="hf-kpi-label">OF bids higher</span>
                    <TrendingUp size={13} style={{ color: 'var(--success)' }} />
                  </div>
                  <span className="hf-kpi-value">{stats.ofAhead}</span>
                  <span className="hf-kpi-sub">{stats.total > 0 ? `${Math.round(stats.ofAhead / stats.total * 100)}% of total` : '—'}</span>
                </div>
                <div className="hf-kpi">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span className="hf-kpi-label">OT bids higher</span>
                    <TrendingDown size={13} style={{ color: 'var(--danger)' }} />
                  </div>
                  <span className="hf-kpi-value">{stats.otAhead}</span>
                  <span className="hf-kpi-sub">{stats.total > 0 ? `${Math.round(stats.otAhead / stats.total * 100)}% of total` : '—'}</span>
                </div>
                <div className="hf-kpi accent">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span className="hf-kpi-label">Suboptimal picks</span>
                    <AlertTriangle size={13} style={{ color: 'var(--warning)' }} />
                  </div>
                  <span className="hf-kpi-value">{stats.suboptimal}</span>
                  <span className="hf-kpi-sub">not on the higher bid</span>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '10px 0' }}>
              <div className="hf-input" style={{ maxWidth: 320 }}>
                <Search size={14} style={{ color: 'var(--fg-3)' }} />
                <input
                  placeholder="Filter by username…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <span className="hf-help" style={{ marginTop: 0 }}>
                {rows.length} of {data?.length ?? 0} dual-source usernames
              </span>
            </div>

            <div className="hf-card flush">
              {isLoading ? (
                <table className="hf-table">
                  <thead>
                    <tr>
                      <th>@username</th>
                      <th className="num">OF cpc</th>
                      <th className="num">OT cpc</th>
                      <th className="num">Δ (OF − OT)</th>
                      <th>Active</th>
                      <th>Set active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 8 }).map((_, i) => <SkelRow key={i} cols={6} numCols={[1, 2]} />)}
                  </tbody>
                </table>
              ) : (data?.length ?? 0) === 0 ? (
                <div style={{ padding: 16 }}>
                  <EmptyState
                    Icon={Users}
                    title="No dual-source usernames yet"
                    description="A username shows up here once it has a live OF order and a live OT order at the same time."
                  />
                </div>
              ) : rows.length === 0 ? (
                <div style={{ padding: 16 }}>
                  <EmptyState
                    compact
                    Icon={Search}
                    title="No matches"
                    description={`No dual-source usernames match "${search}".`}
                    action={<button className="hf-btn ghost sm" onClick={() => setSearch('')}>Clear filter</button>}
                  />
                </div>
              ) : (
                <table className="hf-table">
                  <thead>
                    <tr>
                      <th onClick={() => toggleSort('username')} style={{ cursor: 'pointer' }}>@username{sortIndicator('username')}</th>
                      <th onClick={() => toggleSort('ofCpc')} className="num" style={{ cursor: 'pointer' }}>OF cpc{sortIndicator('ofCpc')}</th>
                      <th onClick={() => toggleSort('otCpc')} className="num" style={{ cursor: 'pointer' }}>OT cpc{sortIndicator('otCpc')}</th>
                      <th onClick={() => toggleSort('delta')} className="num" style={{ cursor: 'pointer' }}>Δ (OF − OT){sortIndicator('delta')}</th>
                      <th>Active</th>
                      <th>Set active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => {
                      const d = delta(row);
                      const active = activeOf(row);
                      const winner = higherBidder(row);
                      const busy = pendingUsername === row.username;
                      return (
                        <tr key={row.username}>
                          <td>
                            <Link
                              to={`/models/${row.ofId}/edit`}
                              className="hf-mono"
                              style={{ color: 'var(--accent)', textDecoration: 'none' }}
                            >
                              @{row.username}
                            </Link>
                          </td>
                          <td className="num hf-mono">{fmtCpc(row.ofCpc)}</td>
                          <td className="num hf-mono">{fmtCpc(row.otCpc)}</td>
                          <td className="num hf-mono" style={{ color: d == null ? 'var(--fg-3)' : d > 0 ? 'var(--success)' : d < 0 ? 'var(--danger)' : 'var(--fg-2)' }}>
                            {d == null ? '—' : `${d > 0 ? '+' : ''}${d.toFixed(4)}`}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span className={`hf-tag sm ${row.activeSource ? 'accent' : 'outline'}`}>
                                {active}{!row.activeSource && ' (default)'}
                              </span>
                              {winner && winner !== active && (
                                <span className="hf-tag warning sm">{winner} bids higher</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <SourceToggle
                              active={active}
                              busy={busy}
                              onSelect={(s) => handleSetActive(row.username, s)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CpcOfOtComparePage;
